/**
 * Shared `YYYY-MM-DD` date-key contract.
 *
 * This format is a URL contract shared with the SMS digest edge function
 * (which builds `/s/:date` links — see `pages/deeplink/ScheduleDateDeepLink.tsx`)
 * and is what `ScheduleTab` seeds its state from via `?date=`. Keep the
 * parsing/validation in one place: two independent copies of this regex +
 * rollover check can silently diverge.
 */

const ISO_DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a `YYYY-MM-DD` string into a local-calendar `Date` at midnight, or
 * `null` if missing/malformed/not a real calendar date.
 *
 * The round-trip check matters: `new Date(y, m - 1, d)` silently rolls
 * over out-of-range components instead of throwing — `new Date(2026, 1, 31)`
 * (Feb 31) becomes March 3, 2026. Re-reading the constructed date's
 * fields and comparing them back against the input catches that.
 */
export function parseDateKey(value: string | null | undefined): Date | null {
  if (!value || !ISO_DATE_KEY.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const parsed = new Date(y, m - 1, d);
  return parsed.getFullYear() === y &&
    parsed.getMonth() === m - 1 &&
    parsed.getDate() === d
    ? parsed
    : null;
}

/** True for a well-formed calendar date that actually exists. */
export function isValidDateKey(value: string | undefined): value is string {
  return parseDateKey(value) !== null;
}
